<script setup lang="ts">
import StyledSearchInput from "@/components/StyledSearchInput.vue";
import useFuse from "@/composables/useFuse";
import useMotionHelpers from "@/composables/useMotionHelpers";
import { useElementSize } from "@vueuse/core";
import { useMotions } from "@vueuse/motion";
import { computed, ref, watch } from "vue";
import { Role } from "../composables/useRoles";

interface Props {
  roles: Role[];
  selectedRole?: Role;
  container?: HTMLDivElement | null;
}

const props = defineProps<Props>();
const searchQuery = ref("");
const listRef = ref<HTMLElement | null>();
const motions = useMotions();
const { width } = useElementSize(props.container);
const { setVariant, touchVariant } = useMotionHelpers();

const filteredRoles = computed(() => {
  if (!searchQuery.value) return props.roles;

  const fuse = useFuse(props.roles, {
    keys: ["name"],
    threshold: 0.4,
    shouldSort: true,
  });
  return fuse.search(searchQuery.value).map((result) => result.item);
});

watch(
  () => props.selectedRole,
  (value) => {
    searchQuery.value = "";
    if (value) {
      setVariant(motions.roleList, "contractList");
    } else {
      setVariant(motions.roleList, "expandList");
    }
  },
);

watch(width, () => {
  touchVariant({ motionElement: motions.roleList });
});

const variants = {
  expandList: {
    width: width,
    transition: {
      type: "spring",
      stiffness: 250,
      damping: 20,
      mass: 0.3,
    },
  },
  contractList: {
    width: 240,
    transition: {
      type: "spring",
      stiffness: 250,
      damping: 20,
      mass: 0.3,
    },
  },
};
</script>

<template>
  <div
    v-motion="'roleList'"
    :initial="{ opacity: 0 }"
    :enter="{
      opacity: 1,
    }"
    :variants="{
      expandList: variants.expandList,
      contractList: variants.contractList,
    }"
    ref="listRef"
    :class="['w-full flex flex-col gap-3', selectedRole && '!hidden md:!flex']"
  >
    <nav v-if="!selectedRole" class="flex justify-between items-center">
      <p>Roles: {{ roles.length }}</p>

      <StyledSearchInput v-model="searchQuery" placeholder="Buscar roles" />
    </nav>

    <div class="flex flex-col gap-3 h-fit">
      <template v-if="filteredRoles.length">
        <template v-for="role in filteredRoles" :key="role.id">
          <slot name="default" :role="role" />
        </template>
      </template>

      <template v-else>
        <p class="text-gray-600">No hay roles registrados</p>
      </template>
    </div>
  </div>
</template>

<style scoped></style>
